import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, uploadImage } from "@/lib/api";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "use-credentials";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

async function cropToFile(src, area) {
  const img = await loadImage(src);
  const size = Math.min(800, Math.round(area.width));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
  return new File([blob], "avatar.jpg", { type: "image/jpeg" });
}

export const AvatarCropper = ({ src, onClose, onDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const onCropComplete = useCallback((_, px) => setArea(px), []);

  const save = async () => {
    setBusy(true);
    try {
      onDone(await uploadImage(await cropToFile(src, area)));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="avatar-cropper">
        <DialogHeader><DialogTitle className="font-display text-2xl">Adjust your photo</DialogTitle></DialogHeader>
        <p className="text-sm text-[var(--hb-muted)] -mt-2">Drag to move, use the slider to zoom so your face fits the circle.</p>
        <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-[var(--hb-ink)]">
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
          )}
        </div>
        <label className="flex items-center gap-3">
          <span className="label-accent">Zoom</span>
          <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-[#C4C600]" data-testid="avatar-zoom-slider" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="avatar-crop-cancel">Cancel</button>
          <button type="button" className="btn-primary" disabled={busy || !area} onClick={save} data-testid="avatar-crop-save">
            {busy ? "Saving..." : "Use this photo"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
